import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Star } from "lucide-react";
import { PawRating } from "./QuickReviewForm";
import { Avatar } from "./Avatar";

/**
 * Renders one Pawground review.
 *
 * New-format reviews carry structured answers (cleanliness, crowdedness,
 * would_return) rendered as pill badges plus the paw rating. Legacy
 * reviews (rating + text only, no structured fields) fall through to
 * star icons + body text so old data still displays correctly.
 */

// Display labels for stored enum values. Anything not in the map is
// rendered verbatim (we'd rather show a raw value than swallow it).
const ANSWER_LABELS = {
  // cleanliness
  spotless: "Spotless",
  clean_enough: "Clean enough",
  needs_work: "Needs work",
  // crowdedness
  empty: "Empty",
  just_right: "Just right",
  too_crowded: "Too crowded",
  // would_return (park-style — service-category colored pill renders separately)
  definitely: "Definitely back",
  maybe: "Maybe back",
  nope: "Not coming back",
};

// Static row of 5 stars showing a sub-rating value. Reused for the
// service-category breakdown (Price / Staff / Wait). Filled stars use
// brand green to match the input UI; empty stars use the warm border
// color so the row reads as a single visual unit with the surrounding
// review card.
const SubRatingRow = ({ label, value, testId }) => (
  <div className="flex items-center gap-2 text-xs" data-testid={testId}>
    <span className="font-body w-24 shrink-0" style={{ color: "#6b6560" }}>
      {label}
    </span>
    <span className="inline-flex items-center gap-0.5" aria-label={`${label}: ${value} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className="w-3.5 h-3.5"
          style={{
            color: n <= value ? "#4A7C59" : "#D4C9B0",
            fill: n <= value ? "#4A7C59" : "transparent",
            strokeWidth: 2,
          }}
        />
      ))}
    </span>
  </div>
);

// Service-category "Would you go back?" colored pill — replaces the
// neutral pill at the bottom of the card whenever the review carries
// at least one sub-rating (i.e. it's a service-category review).
const WOULD_RETURN_PILL = {
  definitely: { label: "Would go back 👍", bg: "#DDEFE0", color: "#2D5C3A", border: "#9BC3A4" },
  maybe:      { label: "Maybe 🤔",          bg: "#FFF4D4", color: "#7A5A1A", border: "#E2C97A" },
  nope:       { label: "Wouldn't return 👎", bg: "#F7DEDE", color: "#7A2D2D", border: "#D89999" },
};

const WouldReturnColoredPill = ({ value, testId }) => {
  const cfg = WOULD_RETURN_PILL[value];
  if (!cfg) return null;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-body font-semibold"
      style={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
      }}
    >
      {cfg.label}
    </span>
  );
};

const Pill = ({ label, testId }) => (
  <span
    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-body font-semibold"
    style={{
      backgroundColor: "#F5F0E8",
      color: "#1A1A0F",
      border: "1px solid #D4C9B0",
    }}
    data-testid={testId}
  >
    {label}
  </span>
);

const ReviewCard = ({ review: r }) => {
  const hasStructured = !!(r.cleanliness || r.crowdedness || r.would_return);
  // A review is "service-category" when it carries at least one
  // numeric sub-rating. The would_return colored pill follows this
  // gate too — only service-category reviews get the colored variant
  // so park/beach/etc. reviews keep their existing neutral pill row.
  const isService = !!(r.price_rating || r.staff_rating || r.wait_rating);
  const initials = (r.owner_name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="border-b border-brand-border-warm pb-4 last:border-0"
      data-testid="review-card"
    >
      <div className="flex items-start gap-3">
        <Avatar src={r.user_photo_url} name={r.owner_name} initials={initials} size={40} fontSize={14} testId="review-user-photo" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-body font-semibold text-sm" style={{ color: "#1A1A0F" }}>
                {r.owner_name}
              </div>
              {r.dog_name && (
                <div className="text-xs flex items-center gap-1.5" style={{ color: "rgba(26,26,15,0.6)" }}>
                  {r.dog_photo_url && <Avatar src={r.dog_photo_url} name={r.dog_name} size={18} testId="review-dog-photo" />}
                  with {r.dog_name}
                  {r.dog_breed ? ` · ${r.dog_breed}` : ""}
                </div>
              )}
            </div>
            <div className="text-xs" style={{ color: "rgba(26,26,15,0.5)" }}>
              {formatDistanceToNow(new Date(r.created_at))} ago
            </div>
          </div>

          {/* Rating row — paws for new reviews, stars for legacy */}
          <div className="mt-2">
            {hasStructured ? (
              <PawRating value={r.rating || 0} size="w-5 h-5" />
            ) : (
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-3.5 h-3.5 ${
                      n <= (r.rating || 0)
                        ? "fill-amber-400 text-amber-400"
                        : "text-brand-border-warm"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Service-category sub-rating breakdown (price / staff /
              wait). Each row is independently optional — if a user
              skipped one, we just skip the row entirely (no empty
              state). Sits between the overall rating and the
              cleanliness/crowdedness pills so the eye reads the
              detailed scores first. */}
          {isService && (
            <div className="mt-3 space-y-1" data-testid="review-subratings">
              {r.price_rating ? (
                <SubRatingRow label="Price / Value" value={r.price_rating} testId="review-subrating-price" />
              ) : null}
              {r.staff_rating ? (
                <SubRatingRow label="Staff & Care" value={r.staff_rating} testId="review-subrating-staff" />
              ) : null}
              {r.wait_rating ? (
                <SubRatingRow label="Wait Time" value={r.wait_rating} testId="review-subrating-wait" />
              ) : null}
              {r.would_return && (
                <div className="pt-1">
                  <WouldReturnColoredPill
                    value={r.would_return}
                    testId={`review-would-return-colored-${r.would_return}`}
                  />
                </div>
              )}
            </div>
          )}

          {/* Quick-tap answer pills */}
          {hasStructured && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="review-pills">
              {r.cleanliness && (
                <Pill
                  label={ANSWER_LABELS[r.cleanliness] || r.cleanliness}
                  testId={`review-pill-cleanliness-${r.cleanliness}`}
                />
              )}
              {r.crowdedness && (
                <Pill
                  label={ANSWER_LABELS[r.crowdedness] || r.crowdedness}
                  testId={`review-pill-crowdedness-${r.crowdedness}`}
                />
              )}
              {/* Park-style would_return neutral pill — suppressed for
                  service-category reviews (those get the colored pill
                  above instead). */}
              {r.would_return && !isService && (
                <Pill
                  label={ANSWER_LABELS[r.would_return] || r.would_return}
                  testId={`review-pill-would-return-${r.would_return}`}
                />
              )}
            </div>
          )}

          {/* Optional comment */}
          {r.text && r.text.trim() && (
            <p
              className="text-sm mt-3 leading-relaxed font-body"
              style={{ color: "rgba(26,26,15,0.85)" }}
            >
              {r.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;
